export interface AudienceSplitBarProps {
	newUsers: number;
	returningUsers: number;
}

/**
 * New-vs-returning audience split (doc 15 §8 traffic contract).
 * One inline stats row — `NEW {n} · RETURNING {n}` (ALL-CAPS tracked muted
 * labels, tabular dash-title counts tinted to their segment) — above ONE
 * two-segment horizontal proportion bar: coral = new, teal = returning
 * (categorical brand pairing, never judgment). Shares round against the
 * PAIR so the two bar segments always fill the whole; the percentages live
 * in the aria-label and the segment widths, not as displayed numbers.
 * Pure display — no click affordance, motion only on data/period change.
 */
export function AudienceSplitBar({ newUsers, returningUsers }: AudienceSplitBarProps) {
	const total = newUsers + returningUsers;
	if (total <= 0) return null;

	const newPct = Math.round((newUsers / total) * 100);
	const returningPct = 100 - newPct;

	return (
		<div className="p-4">
			<div className="flex flex-wrap items-baseline gap-x-2.5">
				<span className="text-dash-label uppercase tracking-wide text-muted-foreground">New</span>
				<span className="text-dash-title font-semibold tabular text-coral-dark">{newUsers.toLocaleString()}</span>
				<span aria-hidden="true" className="text-dash-title text-muted-foreground">·</span>
				<span className="text-dash-label uppercase tracking-wide text-muted-foreground">Returning</span>
				<span className="text-dash-title font-semibold tabular text-teal-dark">{returningUsers.toLocaleString()}</span>
			</div>
			<div
				className="mt-3 flex h-2 w-full overflow-hidden rounded-full bg-muted"
				role="img"
				aria-label={`Audience split: ${newPct}% new, ${returningPct}% returning`}
			>
				<div className="bg-coral transition-all" style={{ width: `${newPct}%` }} />
				<div className="bg-teal transition-all" style={{ width: `${returningPct}%` }} />
			</div>
		</div>
	);
}
